import INVENTORY_API_BASE_URL from "../../config/api";
import React, { useEffect, useState } from "react";
import axios from "axios";
import "./sales.css";
import { CSVLink } from "react-csv";

function Sales() {
  const [orders, setOrders] = useState([]);
  const [selectedDistributor, setSelectedDistributor] = useState("");
  const [matchingSkus, setMatchingSkus] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 20;
  const [skuFilter, setSkuFilter] = useState("");
  const [titleFilter, setTitleFilter] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "" });
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  useEffect(() => {
    fetchOrders();
    fetchMatchingSkus();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(
        `${INVENTORY_API_BASE_URL}/api/get-orders-summary`
      );
      setOrders(response.data);
    } catch (error) {
      console.error("Error fetching orders summary:", error);
    }
  };

  const fetchMatchingSkus = async () => {
    try {
      const response = await axios.get(
        `${INVENTORY_API_BASE_URL}/matching-skus`
      );
      const skuMap = {};
      response.data.matches.forEach((match) => {
        skuMap[match.sku] = {
          batchid: match.batchid,
          purchasePrice: match.purchasePrice,
        };
      });
      setMatchingSkus(skuMap);
    } catch (error) {
      console.error("Error fetching matching SKUs:", error);
    }
  };

  const distributorOptions = [
    ...new Set(orders.map((order) => order.distributorName)),
  ];

  const filteredOrders = (
    selectedDistributor
      ? orders.filter((order) => order.distributorName === selectedDistributor)
      : orders
  )
    .filter((order) => {
      const orderDate = new Date(order.ApprovalDate);
      const from = startDate ? new Date(startDate) : null;
      const to = endDate ? new Date(endDate) : null;
      return (!from || orderDate >= from) && (!to || orderDate <= to);
    })
    .map((order) => ({
      ...order,
      updatedQuantity: order.updatedQuantity.filter(
        (item) =>
          (!skuFilter ||
            item.sku.toLowerCase().includes(skuFilter.toLowerCase())) &&
          (!titleFilter ||
            item.title.toLowerCase().includes(titleFilter.toLowerCase()))
      ),
    }))
    .filter((order) => order.updatedQuantity.length > 0);

  const sortedByDateOrders = [...filteredOrders].sort(
    (a, b) => new Date(b.ApprovalDate) - new Date(a.ApprovalDate)
  );

  const totalPages = Math.ceil(filteredOrders.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentPageData = sortedByDateOrders.slice(startIndex, endIndex);

  const csvData = [];

  filteredOrders.forEach((order) => {
    order.updatedQuantity.forEach((item) => {
      const match = matchingSkus[item.sku];
      const revenue = item.PurchaseValue * item.approvedquantity;
      const printingCostRevenue = match?.purchasePrice
        ? match.purchasePrice * item.approvedquantity
        : "-";
      const marginRevenue = match?.purchasePrice
        ? (item.PurchaseValue - match.purchasePrice) * item.approvedquantity
        : "-";

      csvData.push({
        "Order ID": order.shipmentid,
        "Order Date": new Date(order.ApprovalDate).toLocaleDateString(),
        SKU: item.sku,
        Title: item.title,
        Medium: item.Medium,
        Edition: item.edition,
        MRP: item.mrp,
        "Sales Price": item.PurchaseValue,
        "Printing Cost": match?.purchasePrice || "-",
        "Sales Quantity": item.approvedquantity,
        Revenue: revenue.toFixed(2),
        "Printing Value Sold":
          printingCostRevenue !== "-" ? printingCostRevenue.toFixed(2) : "-",
        Status: "Sale",
        Margin: marginRevenue !== "-" ? marginRevenue.toFixed(2) : "-",
        "Printing Value": match?.purchasePrice
          ? (match.purchasePrice * item.approvedquantity).toFixed(2)
          : "-",
      });
    });
  });

  const sortedOrders = [...filteredOrders].map((order) => ({
    ...order,
    updatedQuantity: [...order.updatedQuantity],
  }));

  if (sortConfig.key && sortConfig.direction) {
    sortedOrders.forEach((order) => {
      order.updatedQuantity.sort((a, b) => {
        const valA = a[sortConfig.key]?.toLowerCase?.() || a[sortConfig.key];
        const valB = b[sortConfig.key]?.toLowerCase?.() || b[sortConfig.key];

        if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
        if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    });
  }

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        if (prev.direction === "asc") return { key, direction: "desc" };
        if (prev.direction === "desc") return { key: "", direction: "" };
      }
      return { key, direction: "asc" };
    });
  };

  return (
    <div className="salesdata-container">
      <h4 style={{ textAlign: "center" }}>Sales Data</h4>
      {orders.length === 0 ? (
        <p>Loading...</p>
      ) : (
        <>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              marginBottom: "20px",
              flexWrap: "nowrap",
              overflowX: "auto",
            }}
          >
            <select
              id="distributorSelect"
              value={selectedDistributor}
              onChange={(e) => {
                setSelectedDistributor(e.target.value);
                setCurrentPage(1);
              }}
              style={{
                width: "200px",
                height: "40px",
                padding: "8px",
                fontSize: "14px",
                borderRadius: "4px",
                border: "1px solid #ccc",
              }}
            >
              <option value="">-- All Distributors --</option>
              {distributorOptions.map((name, idx) => (
                <option key={idx} value={name}>
                  {name}
                </option>
              ))}
            </select>

            <input
              type="date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setCurrentPage(1);
              }}
              style={{
                width: "150px",
                height: "40px",
                padding: "8px",
                fontSize: "14px",
                borderRadius: "4px",
                border: "1px solid #ccc",
                marginLeft: "2rem",
              }}
            />

            <input
              type="date"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                setCurrentPage(1);
              }}
              style={{
                width: "150px",
                height: "40px",
                padding: "8px",
                fontSize: "14px",
                borderRadius: "4px",
                border: "1px solid #ccc",
              }}
            />

            <button
              onClick={() => {
                setStartDate("");
                setEndDate("");
                setCurrentPage(1);
              }}
              style={{
                height: "40px",
                padding: "0 16px",
                fontSize: "14px",
                backgroundColor: "#d9534f",
                color: "#fff",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
            >
              Clear Dates
            </button>

            <CSVLink data={csvData} filename={"sales-summary.csv"}>
              <button
                style={{
                  width: "140px",
                  height: "40px",
                  fontSize: "14px",
                  backgroundColor: "#0c7057",
                  color: "#fff",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  marginLeft: "9rem",
                }}
              >
                Download CSV
              </button>
            </CSVLink>
          </div>

          <div className="order-block">
            <table
              border="1"
              cellPadding="10"
              cellSpacing="0"
              style={{
                width: "100%",
                borderCollapse: "collapse",
                overflowX: "auto",
                display: "block",
                maxWidth: "100%",
                whiteSpace: "nowrap",
                fontSize: "14px",
                border: "1px solid #ccc",
              }}
            >
              <thead style={{ backgroundColor: "#f2f2f2" }}>
                <tr>
                  <th>Order ID</th>
                  <th>Order Date</th>

                  <th
                    onClick={() => handleSort("sku")}
                    style={{ cursor: "pointer", whiteSpace: "nowrap" }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      <span>SKU</span>
                      <input
                        type="text"
                        placeholder="Search SKU"
                        value={skuFilter}
                        onChange={(e) => setSkuFilter(e.target.value)}
                        style={{
                          fontSize: "12px",
                          height: "24px",
                          borderRadius: "4px",
                          border: "1px solid #ccc",
                          padding: "2px",
                          width: "80px",
                        }}
                      />
                    </div>
                  </th>

                  <th
                    onClick={() => handleSort("title")}
                    style={{ cursor: "pointer", whiteSpace: "nowrap" }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      <span>Title</span>
                      <input
                        type="text"
                        placeholder="Search Title"
                        value={titleFilter}
                        onChange={(e) => setTitleFilter(e.target.value)}
                        style={{
                          fontSize: "12px",
                          height: "24px",
                          borderRadius: "4px",
                          border: "1px solid #ccc",
                          padding: "2px",
                          width: "250px",
                        }}
                      />
                    </div>
                  </th>

                  <th>Medium</th>
                  <th>Edition</th>
                  <th>MRP</th>
                  <th>Sales Price</th>
                  <th>Printing Cost</th>
                  <th>Sales Quantity</th>
                  <th>Revenue</th>
                  <th>Printing Value Sold</th>
                  <th>Status</th>
                  <th>Margin</th>
                  <th>Printing Value</th>
                </tr>
              </thead>

              <tbody>
                {currentPageData.map((order, index) =>
                  order.updatedQuantity.map((item, idx) => {
                    const match = matchingSkus[item.sku];
                    const revenue = item.PurchaseValue * item.approvedquantity;
                    const printingCostRevenue = match?.purchasePrice
                      ? match.purchasePrice * item.approvedquantity
                      : "-";
                    const marginRevenue = match?.purchasePrice
                      ? (item.PurchaseValue - match.purchasePrice) *
                        item.approvedquantity
                      : "-";

                    return (
                      <tr key={`${index}-${idx}`}>
                        <td style={{ textAlign: "center" }}>
                          {order.shipmentid}
                        </td>
                        <td style={{ textAlign: "center" }}>
                          {new Date(order.ApprovalDate).toLocaleDateString()}
                        </td>
                        <td style={{ textAlign: "center" }}>{item.sku}</td>
                        <td
                          style={{
                            textAlign: "center",
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                            maxWidth: "300px",
                          }}
                        >
                          {item.title}
                        </td>

                        <td style={{ textAlign: "center" }}>{item.Medium}</td>
                        <td style={{ textAlign: "center" }}>{item.edition}</td>
                        <td style={{ textAlign: "center" }}>{item.mrp}</td>
                        <td style={{ textAlign: "center" }}>
                          {item.PurchaseValue}
                        </td>
                        <td style={{ textAlign: "center" }}>
                          {match ? match.purchasePrice : "-"}
                        </td>
                        <td style={{ textAlign: "center" }}>
                          {item.approvedquantity}
                        </td>
                        <td style={{ textAlign: "center" }}>
                          {revenue.toFixed(2)}
                        </td>
                        <td style={{ textAlign: "center" }}>
                          {printingCostRevenue !== "-"
                            ? printingCostRevenue.toFixed(2)
                            : "-"}
                        </td>
                        <td style={{ textAlign: "center" }}>Sale</td>
                        <td style={{ textAlign: "center" }}>
                          {marginRevenue !== "-"
                            ? marginRevenue.toFixed(2)
                            : "-"}
                        </td>
                        <td style={{ textAlign: "center" }}>
                          {match?.purchasePrice
                            ? (
                                match.purchasePrice * item.approvedquantity
                              ).toFixed(2)
                            : "-"}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>

            <div style={{ marginTop: "20px", textAlign: "center" }}>
              <button
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                style={{ marginRight: "10px" }}
              >
                Previous
              </button>
              <span>
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() =>
                  setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                }
                disabled={currentPage === totalPages}
                style={{ marginLeft: "10px" }}
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default Sales;
